import { useEffect, useState } from "preact/hooks";
import { effect } from "@preact/signals";
import { Header } from "./components/Header";
import { FilterPanel } from "./components/FilterPanel";
import { ResultsList } from "./components/ResultsList";
import { Events } from "./components/Events";
import { Detail, VenueDetail } from "./components/Detail";
import { MapView } from "./components/Map";
import { loadAllData } from "./data/loader";
import { initFromURL, startURLSync } from "./state/url-sync";
import { dataError, eventsOpen, filtersOpen, selected, selectedVenue, theme } from "./state/store";

/** "Offline-ready" once a service worker controls the page (data + shell precached; map
 *  tiles you have looked at are kept). */
function OfflineBadge() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const check = () => setReady(!!navigator.serviceWorker.controller);
    check();
    navigator.serviceWorker.addEventListener("controllerchange", check);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", check);
  }, []);
  if (!ready) return null;
  return <span class="offline-badge" title="The app, all site data and the map areas you've viewed are cached on this device.">✓ works offline</span>;
}

export function App() {
  useEffect(() => {
    try { initFromURL(); startURLSync(); } catch (err) { console.error("url state failed", err); }
    loadAllData().catch((err) => console.error("data load failed", err));
  }, []);

  useEffect(() => {
    const dispose = effect(() => {
      document.documentElement.dataset.theme = theme.value;
      document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.value === "dark" ? "#131a22" : "#f4f1ea");
    });
    return dispose;
  }, []);

  // Body scroll lock while the mobile filter drawer is open.
  useEffect(() => {
    const dispose = effect(() => {
      document.body.classList.toggle("drawer-open", filtersOpen.value);
    });
    return dispose;
  }, []);

  // Keep --header-h honest: sticky offsets (map, results header, detail) depend on it
  // and the header wraps differently across widths.
  useEffect(() => {
    const header = document.querySelector<HTMLElement>(".app-header");
    if (!header) return;
    const apply = () => document.documentElement.style.setProperty("--header-h", `${header.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  const sel = selected.value;
  const venue = selectedVenue.value;
  const err = dataError.value;

  return (
    <div class="app">
      <Header />
      {err && <div class="notice" role="alert">{err}</div>}
      <main class="split">
        <div class="map-pane"><MapView /></div>
        <aside class="side" aria-label="Filters and results">
          <FilterPanel />
          {eventsOpen.value ? <Events /> : <ResultsList />}
        </aside>
        {sel ? <Detail f={sel} /> : venue ? <VenueDetail v={venue} /> : null}
      </main>
      <footer class="app-footer">
        <span>Map © OpenStreetMap contributors, tiles via Protomaps, sessions © The Session (ODbL), photos as credited</span>
        <span><OfflineBadge /> Curated for real trips — corrections welcome.</span>
      </footer>
      {filtersOpen.value && <div class="drawer-backdrop" onClick={() => { filtersOpen.value = false; }} />}
    </div>
  );
}
